import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle } from 'lucide-react';
import LogomarkPattern from '../../components/LogomarkPattern';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin === 'true') {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        // Store admin session
        sessionStorage.setItem('isAdmin', 'true');
        sessionStorage.setItem('adminPassword', password);
        navigate('/admin/dashboard');
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0a4447',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <LogomarkPattern opacity={0.05} density="sparse" tint="gold" />
      
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '48px 40px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        zIndex: 2
      }}>
        {/* Logo/Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          backgroundColor: '#e8f4f5',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <Lock size={28} style={{ color: '#0D7377' }} />
        </div>

        <h1 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: '1.75rem',
          color: '#0a4447',
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          Admin Access
        </h1>
        <p style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '1rem',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          Enter your password to manage blog posts
        </p>

        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px'
          }}>
            <AlertCircle size={18} style={{ color: '#dc3545' }} />
            <span style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              color: '#dc3545'
            }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '8px',
                border: '2px solid rgba(13, 115, 119, 0.2)',
                fontFamily: 'Lato, sans-serif',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box',
                color: '#111827',
                backgroundColor: '#ffffff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0D7377'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(13, 115, 119, 0.2)'}
              data-testid="admin-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: loading ? '#6b7280' : '#0D7377',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Lato, sans-serif',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#0a5d60')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#0D7377')}
            data-testid="admin-login-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '0.875rem',
          color: '#9ca3af',
          textAlign: 'center',
          marginTop: '32px'
        }}>
          Resolv Digital Blog Admin
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
